import { DOCUMENT } from "@angular/common";
import { DestroyRef, inject, Injectable, InjectionToken } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta, Title } from "@angular/platform-browser";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { EventInfo } from "../../types/events-management-request.type";
import {
  DEFAULT_PUBLIC_ORIGIN,
  explicitDate,
  publicStaticPath,
  safeImageUrl,
  trustedOrigin,
} from "./seo-policy";

export const PUBLIC_SITE_ORIGIN = new InjectionToken<string>(
  "Trusted public site origin",
  {
    providedIn: "root",
    factory: () => DEFAULT_PUBLIC_ORIGIN,
  },
);

@Injectable({ providedIn: "root" })
export class EventSeoService {
  private readonly doc = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly origin = trustedOrigin(inject(PUBLIC_SITE_ORIGIN));
  private current: EventInfo | null = null;

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.clear();
          if (publicStaticPath(event.url.split(/[?#]/)[0]))
            this.meta.updateTag({ name: "robots", content: "index, follow" });
        }
        if (
          event instanceof NavigationEnd &&
          this.current &&
          event.urlAfterRedirects.split(/[?#]/)[0] ===
            `/event/${this.current.id}`
        ) {
          this.title.setTitle(
            `${this.text(this.current.title, 200)} | Yes7 Tickets`,
          );
        }
      });
  }

  setEvent(event: EventInfo): void {
    this.clear();
    if (
      !this.origin ||
      event.is_private !== false ||
      event.status !== "published" ||
      !Number.isSafeInteger(event.id) ||
      event.id <= 0
    )
      return;
    this.current = event;
    const name = this.text(event.title, 200);
    const description = this.text(event.description, 300);
    const canonical = `${this.origin}/event/${event.id}`;
    this.title.setTitle(`${name} | Yes7 Tickets`);
    this.meta.updateTag({ name: "robots", content: "index, follow" });
    this.meta.updateTag({ name: "description", content: description });
    for (const [property, content] of Object.entries({
      "og:type": "website",
      "og:title": name,
      "og:description": description,
      "og:url": canonical,
      "og:site_name": "Yes7 Tickets",
      "og:locale": "pt_BR",
    })) {
      this.meta.updateTag({ property, content });
    }
    const image = safeImageUrl(event.thumbnail, this.origin);
    this.meta.updateTag({
      name: "twitter:card",
      content: image ? "summary_large_image" : "summary",
    });
    this.meta.updateTag({ name: "twitter:title", content: name });
    this.meta.updateTag({ name: "twitter:description", content: description });
    if (image) {
      this.meta.updateTag({ property: "og:image", content: image });
      this.meta.updateTag({ name: "twitter:image", content: image });
    }
    const link = this.doc.createElement("link");
    link.rel = "canonical";
    link.href = canonical;
    link.setAttribute("data-marketing-seo", "true");
    this.doc.head.appendChild(link);
    const address = event.address;
    // Google event rich results currently require physical, public events. Never infer offers.
    if (
      !event.online &&
      explicitDate(event.start_date) &&
      address?.location_name &&
      address.city_name &&
      address.street &&
      address.country
    ) {
      const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Event",
        name,
        description,
        url: canonical,
        startDate: event.start_date,
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: this.text(address.location_name, 200),
          address: {
            "@type": "PostalAddress",
            streetAddress: [address.street, address.number]
              .filter(Boolean)
              .join(", "),
            addressLocality: address.city_name,
            addressRegion: address.state,
            postalCode: address.postal_code,
            addressCountry: address.country,
          },
        },
      };
      if (
        explicitDate(event.end_date) &&
        Date.parse(event.end_date) >= Date.parse(event.start_date)
      )
        schema["endDate"] = event.end_date;
      if (image) schema["image"] = [image];
      if (event.organizer_name)
        schema["organizer"] = {
          "@type": "Organization",
          name: this.text(event.organizer_name, 200),
        };
      const script = this.doc.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-marketing-seo", "true");
      script.textContent = JSON.stringify(schema)
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/&/g, "\\u0026");
      this.doc.head.appendChild(script);
    }
  }

  clear(): void {
    this.current = null;
    this.doc
      .querySelectorAll("[data-marketing-seo]")
      .forEach((node) => node.remove());
    for (const key of [
      "description",
      "twitter:card",
      "twitter:title",
      "twitter:description",
      "twitter:image",
    ]) {
      this.meta.removeTag(`name="${key}"`);
    }
    for (const key of [
      "og:type",
      "og:title",
      "og:description",
      "og:url",
      "og:site_name",
      "og:locale",
      "og:image",
    ]) {
      this.meta.removeTag(`property="${key}"`);
    }
    this.meta.updateTag({ name: "robots", content: "noindex, nofollow" });
    this.title.setTitle("Yes7 Tickets");
  }

  private text(value: string, limit: number): string {
    const parsed = this.doc.createElement("div");
    parsed.innerHTML = String(value || "").slice(0, 100000);
    parsed
      .querySelectorAll("script, style, iframe, object")
      .forEach((node) => node.remove());
    return (parsed.textContent || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, limit);
  }
}
